import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { fetchServerStatus } from "../components/ServerStatusWidget";
import {
  Crown, Trophy, Users, Shield, Target, Zap, Star, Server,
  ArrowRight, Flame, Crosshair, MessageSquare, ChevronRight,
  Activity, Award, Swords, Radio, Copy, Check, Play,
  Gamepad2, ShieldCheck, UserPlus, Headphones, RefreshCw, Lock, Map
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import RecentActivityWidget from "../components/RecentActivityWidget";

const DEFAULT_SERVERS = [
  {
    id: "srv_1", address: "209.38.247.243:27015",
    name: "OFFICIAL.INDUNGI.PRO - 2026 NEW MOD", game_type: "cs16",
    dns: "official.indungi.pro", tags: ["Classic", "Ranked", "VIP"],
    desc: "Serverul oficial al comunității. Runde clasice, statistici și acces VIP premium.",
  },
  {
    id: "srv_2", address: "209.38.247.243:27016",
    name: "DOPE.INDUNGI.PRO - ROMANIAN LEGENDS", game_type: "cs16",
    dns: "dope.indungi.pro", tags: ["DOPE", "Ranked", "VIP"],
    desc: "Romanian Legends — server de competiție cu jucători activi și hărți clasice.",
  },
  {
    id: "srv_3", address: "209.38.247.243:27017",
    name: "MIX.INDUNGI.PRO - OFFICIAL MIX SERVER", game_type: "mix",
    dns: "mix.indungi.pro", tags: ["MIX", "5v5", "Competitiv"],
    desc: "Server oficial de mix 5v5. Organizare rapidă, echipe echilibrate, voice chat.",
  },
];

const MAP_IMAGES = {
  de_dust2: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_inferno: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_nuke: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_mirage: "https://image.gametracker.com/images/maps/160x120/cs/de_mirage.jpg",
  de_train: "https://image.gametracker.com/images/maps/160x120/cs/de_train.jpg",
  de_cbble: "https://image.gametracker.com/images/maps/160x120/cs/de_cbble.jpg",
  cs_office: "https://image.gametracker.com/images/maps/160x120/cs/cs_office.jpg",
};

const GAME_ICONS = {
  cs16: "https://1shot2kill.pl/uploads/monthly_2026_03/16_cs.webp.7bb969a18166855fda8b88509e32c674.webp",
  mix: "https://1shot2kill.pl/uploads/monthly_2026_03/ikonka3.webp.61be5e01f5474ae721b2f546154d3a5f.webp",
  cs2: "https://1shot2kill.pl/uploads/monthly_2026_03/2_cs.webp.cdc7c2c51eadcf0df28c4aa7d90c851d.webp",
};

function getMapImg(map = "") {
  const m = map.toLowerCase();
  for (const [k, v] of Object.entries(MAP_IMAGES)) { if (m.includes(k.split("_")[1])) return v; }
  return `https://image.gametracker.com/images/maps/160x120/cs/${m}.jpg`;
}

function AnimatedCounter({ target, suffix = "+" }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!target || started.current) return;
    started.current = true;
    let cur = 0;
    const step = Math.max(1, Math.ceil(target / 50));
    const t = setInterval(() => {
      cur = Math.min(cur + step, target);
      setCount(cur);
      if (cur >= target) clearInterval(t);
    }, 25);
    return () => clearInterval(t);
  }, [target]);
  return <>{count.toLocaleString()}{suffix}</>;
}

/* ─── Live Server Card (Free-Arena style) ─── */
function ServerSpotlightCard({ server }) {
  const [copied, setCopied] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["serverStatus", server.address],
    queryFn: () => fetchServerStatus(server.address),
    staleTime: 0,
    refetchInterval: 5000,
  });

  const isOnline = data?.online === true;
  const players = data?.players ?? 0;
  const maxPlayers = data?.max_players ?? 32;
  const currentMap = data?.map || "de_dust2";
  const fillPct = maxPlayers > 0 ? Math.round((players / maxPlayers) * 100) : 0;
  const gameType = server.game_type || "cs16";

  const handleConnect = () => { window.location.href = `steam://connect/${server.address}`; };
  const handleCopy = () => {
    navigator.clipboard.writeText(server.address);
    setCopied(true);
    toast.success(`Copiat: ${server.address}`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-[#0b0d16] hover:border-orange-500/30 transition-all duration-300 shadow-2xl">
      {/* Card glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/[0.04] to-transparent pointer-events-none" />
      {/* Scanline */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
        backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(255,255,255,0.04) 3px,rgba(255,255,255,0.04) 4px)"
      }} />

      {/* Cover image */}
      <div className="relative min-h-[180px] overflow-hidden">
        <img
          src={getMapImg(currentMap)}
          alt={currentMap}
          onError={e => { e.target.onerror = null; e.target.src = ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"); }}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d16] via-[#0b0d16]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

        {/* Top row: icon + status */}
        <div className="relative z-10 flex items-start justify-between p-4">
          <div className="w-14 h-14 rounded-xl border border-orange-500/30 bg-orange-500/10 flex items-center justify-center shadow-[0_0_20px_rgba(255,79,0,0.2)]">
            <img src={GAME_ICONS[gameType] || GAME_ICONS.cs16} alt={gameType} className="w-8 h-8 object-contain" />
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest border ${
            isLoading ? "border-zinc-700 bg-zinc-900 text-zinc-500" :
            isOnline ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" :
            "border-red-500/40 bg-red-500/10 text-red-400"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isLoading ? "bg-zinc-600" : isOnline ? "bg-emerald-400 shadow-[0_0_6px_#34d399]" : "bg-red-500"} ${isOnline && !isLoading ? "animate-pulse" : ""}`} />
            {isLoading ? "Se verifică" : isOnline ? "LIVE" : "OFFLINE"}
          </span>
        </div>

        {/* Bottom: server name */}
        <div className="relative z-10 px-4 pb-4 mt-8">
          <p className="text-[10px] font-black uppercase tracking-widest text-orange-400/70 mb-1 notranslate" translate="no">INDUNGI.PRO</p>
          <h3 className="font-['Orbitron'] text-2xl font-extrabold uppercase leading-none text-white truncate">
            {server.name.split(" - ")[1] || server.name.split(" - ")[0]}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-4 gap-4">
        <p className="text-sm text-zinc-400 leading-relaxed">{server.desc}</p>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-white/6 bg-white/[0.03] p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-600 mb-1.5">
              <Users className="w-3.5 h-3.5 text-orange-500" /> Jucători
            </div>
            <div className="font-['Orbitron'] text-lg font-extrabold text-white">
              {isLoading ? "—" : <><span className={players > 0 ? "text-emerald-400" : ""}>{players}</span><span className="text-zinc-600"> / {maxPlayers}</span></>}
            </div>
          </div>
          <div className="rounded-xl border border-white/6 bg-white/[0.03] p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-600 mb-1.5">
              <Map className="w-3.5 h-3.5 text-orange-500" /> Hartă
            </div>
            <div className="font-['Orbitron'] text-sm font-extrabold text-white uppercase truncate">
              {isLoading ? "—" : currentMap}
            </div>
          </div>
        </div>

        {/* Player bar */}
        <div>
          <div className="flex justify-between text-[10px] font-mono text-zinc-600 mb-1.5">
            <span>Ocupare server</span><span>{fillPct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${fillPct}%`,
                background: fillPct > 75 ? "#ef4444" : fillPct > 40 ? "#f97316" : "#34d399"
              }}
            />
          </div>
        </div>

        {/* IP row */}
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600 mb-1.5">Host / IP</p>
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/6 bg-white/[0.03] font-mono text-sm font-bold text-white">
            <Radio className="w-3.5 h-3.5 text-orange-500 shrink-0" />
            <span className="truncate flex-1">{server.dns || server.address}</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {(server.tags || []).map(t => (
            <span key={t} className="px-2.5 py-1 rounded-lg border border-white/8 bg-white/[0.03] text-[10px] font-black uppercase tracking-wider text-zinc-500">{t}</span>
          ))}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 mt-auto pt-2">
          <button
            onClick={handleConnect}
            disabled={!isOnline}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
              isOnline
                ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white shadow-[0_0_20px_rgba(255,79,0,0.3)] hover:shadow-[0_0_30px_rgba(255,79,0,0.5)]"
                : "bg-white/[0.04] border border-white/8 text-zinc-600 cursor-not-allowed"
            }`}
          >
            {isOnline ? <Play className="w-3.5 h-3.5 fill-current" /> : <Lock className="w-3.5 h-3.5" />}
            {isOnline ? "CONNECT" : "Offline"}
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-xs font-black uppercase tracking-wider text-white transition-all"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copiat!" : "Copiază IP"}
          </button>
          <Link
            to="/servere"
            className="col-span-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/8 hover:border-orange-500/30 bg-white/[0.02] hover:bg-orange-500/5 text-xs font-black uppercase tracking-wider text-zinc-400 hover:text-orange-400 transition-all"
          >
            Detalii server <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}

/* ─── Main Landing Page ─── */
export default function Landing() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [topUsers, setTopUsers] = useState([]);
  const [vips, setVips] = useState([]);

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/users/top?limit=5").then(r => {
      if (r.data?.length > 0) setTopUsers(r.data);
      else api.get("/leaderboard").then(l => setTopUsers((l.data || []).slice(0, 5))).catch(() => {});
    }).catch(() => {});
    api.get("/users/vip?limit=6").then(r => setVips(r.data || [])).catch(() => {});
  }, []);

  const totalUsers = stats.user_count || stats.users || 28;
  const totalTopics = stats.topic_count || stats.topics || 27;
  const totalPosts = stats.post_count || stats.posts || 28;
  const onlineNow = stats.online_now || 1;

  return (
    <div className="relative overflow-hidden" data-testid="landing-page">

      {/* ══════════════════════════════════════════
          ATMOSPHERIC BACKGROUND (ember particles)
      ══════════════════════════════════════════ */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_18%_18%,rgba(255,79,0,0.18),transparent_38%),radial-gradient(ellipse_at_82%_10%,rgba(255,140,0,0.10),transparent_34%),radial-gradient(ellipse_at_50%_100%,rgba(255,60,0,0.15),transparent_55%)]" />
        {/* Ember particles */}
        {[6,14,23,37,51,63,74,86,94,30,58,79].map((left, i) => (
          <span key={i} className="absolute bottom-0 rounded-full bg-orange-500 opacity-0"
            style={{
              left: `${left}%`,
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              animation: `ember-rise ${6 + (i * 0.7) % 4}s ${(i * 0.4) % 3}s infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes ember-rise {
            0% { transform: translateY(0) translateX(0); opacity: 0; }
            15% { opacity: 0.7; }
            85% { opacity: 0.3; }
            100% { transform: translateY(-80vh) translateX(${20}px); opacity: 0; }
          }
        `}</style>
        {/* Scanlines */}
        <div className="absolute inset-0 opacity-[0.018]" style={{
          backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,0.06) 2px,rgba(255,255,255,0.06) 4px)"
        }} />
      </div>

      {/* ══════════════════════════════════════════
          SECTION 1 — HERO FULL SCREEN
      ══════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col overflow-hidden border-b border-orange-500/10" data-testid="landing-hero">
        {/* Hero BG */}
        <div className="absolute inset-0 -z-10 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url(/hero_bg.jpg)" }} />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(0,0,0,0.95)_0%,rgba(0,0,0,0.80)_35%,rgba(0,0,0,0.40)_60%,rgba(0,0,0,0.70)_100%)]" />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(900px_500px_at_18%_48%,rgba(255,50,0,0.18),transparent_68%),radial-gradient(600px_400px_at_82%_32%,rgba(0,120,255,0.08),transparent_65%)]" />
        {/* Top neon line */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/60 to-transparent" />
        {/* Bottom neon line */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />

        {/* Main Hero Content — aligned to bottom like Free Arena */}
        <div className="relative z-10 flex flex-col justify-end flex-1 max-w-[96rem] mx-auto w-full px-4 sm:px-6 lg:px-10 pb-0 pt-24">
          <div className="max-w-4xl pb-10 sm:pb-14">
            {/* Kicker badge */}
            <p className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-orange-500/30 bg-orange-500/10 backdrop-blur-sm text-[11px] font-black uppercase tracking-[0.18em] text-orange-300">
              <Gamepad2 className="w-3.5 h-3.5" />
              <span className="notranslate" translate="no">INDUNGI.PRO</span> — Comunitate CS 1.6 Românească
            </p>

            {/* Main title */}
            <h1
              className="font-['Orbitron'] font-black uppercase leading-[0.78] text-white notranslate"
              style={{ fontSize: "clamp(3.4rem,10vw,8.5rem)" }}
              translate="no"
            >
              INDUNGI
            </h1>
            <p className="font-['Orbitron'] font-semibold leading-tight text-white/90 mt-3" style={{ fontSize: "clamp(1.5rem,3vw,2.6rem)" }}>
              CS 1.6, Mix Competitiv și comunitate
            </p>
            <p className="font-['Orbitron'] font-black uppercase leading-tight mt-1" style={{ fontSize: "clamp(1.4rem,3.2vw,2.4rem)" }}>
              <span className="text-orange-500">Joacă azi.</span>{" "}
              <span className="text-amber-400">Urcă în top.</span>{" "}
              <span className="text-white">Rămâi cu noi.</span>
            </p>
            {/* Hero underline */}
            <div className="mt-3 h-px max-w-2xl bg-gradient-to-r from-orange-500 via-amber-400 to-transparent" />

            <p className="mt-6 max-w-3xl text-base font-medium leading-7 text-white/72 sm:text-lg">
              Alege serverul, copiază IP-ul, intră pe voice sau aplică pentru staff.
              <span className="notranslate" translate="no">INDUNGI</span> îți arată statusul live, topul lunar și canalele reale ale comunității.
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/servere"
                className="inline-flex min-h-14 items-center justify-center gap-3 px-7 py-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white text-sm font-black uppercase tracking-[0.14em] shadow-[0_0_30px_rgba(255,79,0,0.4)] hover:shadow-[0_0_50px_rgba(255,79,0,0.7)] transition-all hover:scale-[1.02]"
                data-testid="landing-join"
              >
                <Gamepad2 className="w-5 h-5" />
                Joacă acum
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://discord.gg/CrGC77X8W"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex min-h-14 items-center justify-center gap-3 px-7 py-4 rounded-xl border border-[#5865F2]/50 bg-[#5865F2]/15 hover:border-[#5865F2] hover:bg-[#5865F2]/25 text-white text-sm font-black uppercase tracking-[0.14em] transition-all"
              >
                <img src="https://1shot2kill.pl/uploads/monthly_2026_03/dc.webp.3c14a202eef4806843f6d2f64422f7db.webp" alt="Discord" className="w-5 h-5" />
                Discord
              </a>
              {!user && (
                <Link
                  to="/register"
                  className="inline-flex min-h-14 items-center justify-center gap-3 px-7 py-4 rounded-xl border border-orange-500/40 bg-orange-500/10 hover:border-orange-500 hover:bg-orange-500/20 text-white text-sm font-black uppercase tracking-[0.14em] transition-all"
                >
                  <ShieldCheck className="w-5 h-5" />
                  Creează cont
                </Link>
              )}
            </div>
          </div>

          {/* ── Feature Bar (4 cards at bottom of hero) ── */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-orange-500/10 bg-black/50 backdrop-blur-md divide-y sm:divide-y-0 sm:divide-x divide-white/6">
            {[
              { icon: Server, label: "Alege serverul", sub: "Status live, IP-uri și conectare rapidă pentru serverele active.", link: "/servere", cta: "Vezi serverele" },
              { icon: Trophy, label: "Urcă în top", sub: "Postează, primești reacții și câștigă puncte. Top lunar cu premii VIP.", link: "/leaderboard", cta: "Vezi topul" },
              { icon: UserPlus, label: "Intră în staff", sub: "Aplicații deschise pentru admini activi, maturi și implicați.", link: "/forums", cta: "Aplică staff" },
              { icon: Headphones, label: "Stai aproape", sub: "Discord, forum și canale de voice pentru anunțuri și suport.", href: "https://discord.gg/CrGC77X8W", cta: "Intră pe Discord" },
            ].map(({ icon: Icon, label, sub, link, href, cta }) => {
              const El = href ? "a" : Link;
              const props = href ? { href, target: "_blank", rel: "noopener noreferrer" } : { to: link };
              return (
                <El key={label} {...props}
                  className="group relative flex min-h-36 gap-4 px-5 py-6 lg:px-6 text-left transition hover:bg-orange-500/[0.05]"
                >
                  <span className="grid w-14 h-14 shrink-0 place-items-center rounded-xl border border-orange-500/25 bg-orange-500/8 text-orange-400 shadow-[0_0_20px_rgba(255,79,0,0.10)] transition group-hover:border-orange-500/60 group-hover:text-white">
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <strong className="block text-sm font-black uppercase tracking-[0.08em] text-white">{label}</strong>
                    <span className="mt-1.5 block text-xs leading-5 text-white/50">{sub}</span>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-3 text-xs font-black uppercase tracking-[0.14em] text-orange-400">
                      {cta} <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
                    </span>
                  </span>
                </El>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          NEW 2-COLUMN PORTAL LAYOUT
      ══════════════════════════════════════════ */}
      <div className="max-w-[96rem] mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          
          {/* Main Column (Left) */}
          <div className="space-y-12">
            
            {/* Servers List */}
            <section data-testid="landing-servers">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
                <div>
                  <h2 className="font-['Orbitron'] text-3xl font-black uppercase text-white mb-2 flex items-center gap-3">
                    <Server className="w-8 h-8 text-orange-500" />
                    Server Network
                  </h2>
                  <p className="text-zinc-400 text-sm">Status live și conectare rapidă pe serverele oficiale.</p>
                </div>
                <Link to="/servere" className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 hover:border-orange-500/40 bg-white/[0.03] hover:bg-orange-500/8 text-xs font-black uppercase tracking-[0.14em] text-zinc-400 hover:text-orange-400 transition-all">
                  <RefreshCw className="w-3.5 h-3.5" /> Toate serverele
                </Link>
              </div>
              
              {/* Server Cards Grid */}
              <div className="grid gap-6 md:grid-cols-2">
                {DEFAULT_SERVERS.map(s => <ServerSpotlightCard key={s.id} server={s} />)}
              </div>
            </section>

            {/* Recent Activity */}
            <section data-testid="landing-recent">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
                <div>
                  <h2 className="font-['Orbitron'] text-3xl font-black uppercase text-white mb-2 flex items-center gap-3">
                    <MessageSquare className="w-8 h-8 text-orange-500" />
                    Ultimele Discuții
                  </h2>
                  <p className="text-zinc-400 text-sm">Cele mai noi subiecte dezbătute pe forum de membrii comunității.</p>
                </div>
                <Link to="/forums" className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 hover:border-orange-500/40 bg-white/[0.03] hover:bg-orange-500/8 text-xs font-black uppercase tracking-[0.14em] text-zinc-400 hover:text-orange-400 transition-all">
                   Intră pe forum
                </Link>
              </div>
              <RecentActivityWidget />
            </section>
          </div>

          {/* Sidebar (Right) */}
          <div className="space-y-6">
            
            {/* Stats Box */}
            <div className="rounded-2xl border border-white/8 bg-[#0b0d16] p-6 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-500" />
              <h3 className="font-['Orbitron'] text-lg font-extrabold uppercase text-white mb-5 flex items-center gap-2">
                <Activity className="w-4 h-4 text-orange-500" /> Statistici
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-white/5">
                  <span className="text-sm font-mono text-zinc-400 uppercase">Membri</span>
                  <span className="font-bold text-white"><AnimatedCounter target={totalUsers} /></span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/5">
                  <span className="text-sm font-mono text-zinc-400 uppercase">Topicuri</span>
                  <span className="font-bold text-white"><AnimatedCounter target={totalTopics} /></span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/5">
                  <span className="text-sm font-mono text-zinc-400 uppercase">Postări</span>
                  <span className="font-bold text-white"><AnimatedCounter target={totalPosts} /></span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-mono text-zinc-400 uppercase flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]" /> Online
                  </span>
                  <span className="font-bold text-emerald-400">{onlineNow}</span>
                </div>
              </div>
            </div>

            {/* Discord Box */}
            <div className="relative rounded-2xl border border-[#5865F2]/25 bg-gradient-to-br from-[#5865F2]/10 to-transparent p-6 shadow-xl overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#5865F2]" />
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#5865F2]/20 blur-2xl rounded-full pointer-events-none group-hover:bg-[#5865F2]/30 transition-all" />
              <h3 className="font-['Orbitron'] text-lg font-extrabold uppercase text-white mb-3 flex items-center gap-2 relative z-10">
                <img src="https://1shot2kill.pl/uploads/monthly_2026_03/dc.webp.3c14a202eef4806843f6d2f64422f7db.webp" alt="Discord" className="w-5 h-5" />
                Discord
              </h3>
              <p className="text-zinc-400 text-sm mb-5 relative z-10">Canale voice active, asistență live și chat competitiv.</p>
              <a href="https://discord.gg/CrGC77X8W" target="_blank" rel="noopener noreferrer" className="relative z-10 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#5865F2] hover:bg-[#4752c4] text-white font-black text-sm uppercase tracking-wider transition-all">
                Conectează-te
              </a>
            </div>

            {/* Top Members */}
            <div className="rounded-2xl border border-white/8 bg-[#0b0d16] p-6 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-500 to-amber-600" />
              <div className="absolute -bottom-10 -right-10 opacity-[0.03]"><Trophy className="w-48 h-48 text-orange-500" /></div>
              <h3 className="font-['Orbitron'] text-lg font-extrabold uppercase text-white mb-5 flex items-center gap-2 relative z-10">
                <Trophy className="w-4 h-4 text-amber-500" /> Top Membri
              </h3>
              <div className="space-y-3 relative z-10">
                {topUsers.length > 0 ? topUsers.slice(0, 5).map((u, i) => (
                  <Link to={`/u/${u.username}`} key={u.id || i} className="flex items-center gap-3 p-2.5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-orange-500/5 transition-all group">
                    <span className={`font-['Orbitron'] font-black w-5 text-center shrink-0 ${i === 0 ? "text-amber-400" : i === 1 ? "text-zinc-300" : i === 2 ? "text-amber-700" : "text-zinc-600"}`}>#{i+1}</span>
                    <Avatar user={u} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white group-hover:text-orange-400 truncate leading-none mb-1"><Username user={u} asText /></div>
                      <div className="text-[10px] text-zinc-500 font-mono">{u.points || 0} pct</div>
                    </div>
                  </Link>
                )) : <div className="text-center text-xs text-zinc-600">Se încarcă...</div>}
              </div>
            </div>

            {/* VIP Members */}
            <div className="rounded-2xl border border-white/8 bg-[#0b0d16] p-6 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-yellow-600" />
              <div className="absolute -bottom-10 -right-10 opacity-[0.03]"><Crown className="w-48 h-48 text-amber-400" /></div>
              <h3 className="font-['Orbitron'] text-lg font-extrabold uppercase text-white mb-5 flex items-center gap-2 relative z-10">
                <Crown className="w-4 h-4 text-amber-400" /> VIP Hall of Fame
              </h3>
              <div className="grid grid-cols-4 gap-3 relative z-10">
                {vips.length > 0 ? vips.slice(0, 8).map(v => (
                  <Link to={`/u/${v.username}`} key={v.id} className="flex justify-center group" title={v.username}>
                    <Avatar user={v} size={48} hover />
                  </Link>
                )) : <div className="col-span-4 text-center text-xs text-zinc-600 py-4">Niciun VIP activ.</div>}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
